import {Drawer} from '@/components';
import {useEffect, useRef, useState} from 'react';
import {useResponsive, useViewport} from '@/hooks';
import clsx from 'clsx';
import {getHeight} from '@/utils';
import {SidebarChatContent, SidebarChatHeader} from './';
import {useBuddyLayout} from '@/layouts/buddy';
import axios from 'axios';
import {enqueueSnackbar} from 'notistack';

export interface ChatConfig {
    title?: string;
    instructions?: string;
    entityType: 'job' | 'company' | 'candidate' | string;
    entityId: string;
}

export interface Message {
    role: 'user' | 'ai';
    content: string;
    isLoading?: boolean;
    timestamp: string;
}

export interface SidebarChatProps {
    config?: ChatConfig;
    onUpdateComplete?: () => void;
}

export const SidebarChat: React.FC<SidebarChatProps> = ({config, onUpdateComplete}) => {
    const selfRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);
    const [vpHeight] = useViewport();
    const desktop = useResponsive('up', 'lg');
    const scrollOffset = 40;

    const {
        layout,
        setSidebarChatCollapse,
        mobileSidebarChatOpen,
        setMobileSidebarChatOpen,
        registerChat,
        unregisterChat
    } = useBuddyLayout();

    const [collapsedLocal, setCollapsedLocal] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const API_URL = import.meta.env.VITE_APP_API_URL;

    useEffect(() => {
        registerChat();

        if (layout?.options?.sidebarChat?.collapse === true) {
            setSidebarChatCollapse(false);
        }

        setCollapsedLocal(false);

        return () => {
            unregisterChat();
        };
    }, [config, layout?.options?.sidebarChat?.collapse]);

    useEffect(() => {
        if (headerRef.current) {
            const h = getHeight(headerRef.current);
            setHeight(vpHeight - h - scrollOffset);
        }
    }, [vpHeight]);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                if (!config?.entityId || !config?.entityType) return;

                const token = localStorage.getItem('authToken');
                const url = `${API_URL}/${config.entityType}/cps-history/${config.entityId}`;
                const response = await axios.get(url, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: 'application/json',
                    },
                });

                const history = response.data;
                const formattedMessages: Message[] = history.flatMap((entry: any) => [
                    {
                        role: 'user',
                        content: entry.user_instruction,
                        timestamp: entry.created_at,
                    },
                    {
                        role: 'ai',
                        content: entry.ai_explanation,
                        timestamp: entry.created_at,
                    },
                ]);

                setMessages(
                    formattedMessages.sort(
                        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                    )
                );
            } catch (err: any) {
                enqueueSnackbar(`Failed to load chat history: ${err?.message || err}`, {
                    variant: 'error',
                });
            }
        };

        fetchHistory();
    }, [config?.entityId, config?.entityType]);

    const onToggle = () => {
        const next = !collapsedLocal;
        setCollapsedLocal(next);
        setSidebarChatCollapse(next);
    };

    const onMobileClose = () => setMobileSidebarChatOpen(false);

    const renderPanel = () => (
        <div
            ref={selfRef}
            className={clsx(
                'sidebar-chat lg:fixed lg:top-0 lg:bottom-0 lg:end-0',
                'lg:z-20 z-20',
                'flex flex-col bg-light border-l lg:border-l-gray-200',
                'w-[400px]',
                'max-w-full',
                collapsedLocal ? 'translate-x-full' : 'translate-x-0',
                'transition-transform duration-300 ease-in-out'
            )}
        >
            <div className="flex flex-col gap-1 px-6 py-5 h-full overflow-hidden">
                {config && (
                    <>
                        {messages.length === 0 && (
                            <SidebarChatHeader
                                ref={headerRef}
                                title={config.title}
                                instructions={config.instructions}
                                onToggle={onToggle}
                                collapsed={collapsedLocal}
                            />
                        )}

                        <SidebarChatContent
                            height={height}
                            config={config}
                            messages={messages}
                            setMessages={setMessages}
                            onUpdateComplete={onUpdateComplete}
                        />
                    </>
                )}
            </div>
        </div>
    );

    return desktop ? (
        renderPanel()
    ) : (
        <Drawer open={mobileSidebarChatOpen} onClose={onMobileClose} ModalProps={{keepMounted: true}}>
            {renderPanel()}
        </Drawer>
    );
};
